import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, CheckCircle2, ClipboardList, ExternalLink, Mail, RefreshCw, Search, ShieldCheck, Sparkles, Upload } from 'lucide-react'
import Layout from '../../components/Layout'
import Seo from '../../components/Seo'
import { parseOutreachEmailInput } from '../../helpers/outreachEmailInput'
import { outreachService } from '../../services/outreachService'
import DraftWorkspace from './DraftWorkspace'

const formatLocalDateInput = (date = new Date()) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatDate = (value) => value ? new Date(value).toLocaleDateString('cs-CZ') : '—'

const formatPrice = (value) => {
  if (value === null || value === undefined || value === '') return '—'
  if (!Number.isFinite(Number(value))) return '—'
  return `${Math.round(Number(value)).toLocaleString('cs-CZ')} Kč`
}

const formatConfidence = (value) => {
  if (value === null || value === undefined || value === '') return null
  if (!Number.isFinite(Number(value))) return null
  return `${Math.round(Number(value) * 100)} %`
}

const stageLabels = {
  new: 'Nový kontakt',
  research_ready: 'Podklad připraven',
  research_missing: 'Bez inzerátu',
  draft_ready: 'Návrh připraven',
  approved: 'Návrh schválen',
  sent: 'Odesláno',
  delivered: 'Doručeno',
  delivery_delayed: 'Doručení odloženo',
  delivery_failed: 'Doručení selhalo',
  delivery_unknown: 'Stav doručení neznámý',
  suppressed: 'Potlačeno',
}

const ResearchListing = ({ research }) => {
  if (research?.status === 'no_listing_found') {
    return (
      <div>
        <div className="font-medium text-amber-700">Inzerát nenalezen</div>
        <div className="mt-0.5 text-xs text-slate-500">Kontrolováno {formatDate(research.attemptedAt)}</div>
      </div>
    )
  }

  const listing = research?.listing
  if (!listing) return <span className="text-slate-400">Zatím nepřipraveno</span>

  return (
    <div className="min-w-[220px]">
      <a
        href={`/detail/${encodeURIComponent(listing.propertyId)}`}
        className="font-medium text-slate-900 hover:text-violet-700 hover:underline"
      >
        {listing.title}
      </a>
      <div className="mt-0.5 text-xs text-slate-500">{listing.location || 'Lokalita neuvedena'}</div>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium text-slate-700">{formatPrice(listing.price)}</span>
        <span className={listing.inactive ? 'text-amber-700' : 'text-emerald-700'}>
          {listing.inactive ? 'Neaktivní' : 'Aktivní'}
        </span>
        {listing.listingUrl && (
          <a
            href={listing.listingUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-violet-700 hover:underline"
          >
            SReality <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  )
}

const ValuationEvidence = ({ valuation }) => {
  const current = valuation?.scenarios?.current?.summary

  if (!current) {
    return (
      <div className="min-w-[180px]">
        <div className="text-sm text-slate-500">Odhad se připravuje</div>
        <div className="mt-0.5 text-xs text-slate-400">Později použijte Obnovit.</div>
      </div>
    )
  }

  return (
    <div className="min-w-[180px] space-y-1.5 text-xs">
      <div className="flex items-center justify-between gap-3">
        <span className="text-slate-500">Současný stav</span>
        <span className="font-medium text-slate-900">{formatPrice(current.anchorPrice)}</span>
      </div>
      {formatConfidence(current.confidence) && (
        <div className="text-slate-400">Důvěra odhadu {formatConfidence(current.confidence)}</div>
      )}
      {(valuation?.refreshNeeded || valuation?.refreshing) && (
        <div className="text-amber-700">Probíhá aktualizace odhadu</div>
      )}
    </div>
  )
}

const Outreach = () => {
  const [emailInput, setEmailInput] = useState('')
  const [sourceLabel, setSourceLabel] = useState('')
  const [consentCapturedAt, setConsentCapturedAt] = useState(formatLocalDateInput)
  const [consentConfirmed, setConsentConfirmed] = useState(false)
  const [contacts, setContacts] = useState([])
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 })
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [importing, setImporting] = useState(false)
  const [preparingContactId, setPreparingContactId] = useState(null)
  const [selectedContactId, setSelectedContactId] = useState(null)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)

  const parsedInput = useMemo(() => parseOutreachEmailInput(emailInput), [emailInput])
  const selectedContact = useMemo(
    () => contacts.find((contact) => contact._id === selectedContactId) || null,
    [contacts, selectedContactId]
  )

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearch(searchInput.trim())
      setPagination((current) => ({ ...current, page: 1 }))
    }, 300)
    return () => window.clearTimeout(timeoutId)
  }, [searchInput])

  const loadContacts = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const payload = await outreachService.getContacts({
        page: pagination.page,
        limit: pagination.limit,
        search,
      })
      setContacts(payload.contacts || [])
      setPagination(payload.pagination || { page: 1, limit: 50, total: 0, pages: 0 })
    } catch (requestError) {
      console.error('Unable to load outreach contacts:', requestError)
      setError('Kontakty se nepodařilo načíst.')
    } finally {
      setLoading(false)
    }
  }, [pagination.page, pagination.limit, search])

  useEffect(() => {
    loadContacts()
  }, [loadContacts])

  const canImport =
    parsedInput.emails.length > 0 &&
    parsedInput.emails.length <= 500 &&
    parsedInput.invalidEmails.length === 0 &&
    sourceLabel.trim().length >= 2 &&
    Boolean(consentCapturedAt) &&
    consentConfirmed &&
    !importing

  const handleImport = async (event) => {
    event.preventDefault()
    if (!canImport) return

    try {
      setImporting(true)
      setError('')
      setResult(null)
      const payload = await outreachService.importContacts({
        emails: parsedInput.emails,
        sourceLabel: sourceLabel.trim(),
        consentCapturedAt,
        consentConfirmed,
      })

      setResult(payload.summary)
      setEmailInput('')
      setConsentConfirmed(false)
      setPagination((current) => ({ ...current, page: 1 }))
      await loadContacts()
    } catch (requestError) {
      console.error('Unable to import outreach contacts:', requestError)
      const invalid = requestError.details?.invalidEmails
      setError(
        invalid?.length
          ? `Neplatné adresy: ${invalid.join(', ')}`
          : requestError.message || 'Import se nepodařil.'
      )
    } finally {
      setImporting(false)
    }
  }

  const handlePrepareContact = async (contactId) => {
    try {
      setPreparingContactId(contactId)
      setError('')
      const preparedContact = await outreachService.prepareContact(contactId)
      setContacts((current) => current.map((contact) => contact._id === contactId ? preparedContact : contact))
    } catch (requestError) {
      console.error('Unable to prepare outreach contact:', requestError)
      setError(requestError.message || 'Příprava kontaktu se nezdařila.')
    } finally {
      setPreparingContactId(null)
    }
  }

  const handleContactUpdated = (updatedContact) => {
    setContacts((current) => current.map((contact) => contact._id === updatedContact._id ? updatedContact : contact))
  }

  const headerContent = (
    <div className="flex flex-col gap-1">
      <h1 className="text-xl font-semibold text-slate-950 sm:text-2xl">Outreach</h1>
      <p className="text-sm text-slate-500">Ručně dodané kontakty a příprava individuální komunikace.</p>
    </div>
  )

  return (
    <Layout headerContent={headerContent}>
      <Seo
        title="Outreach"
        description="Správa kontaktů pro individuální outreach Realitního Jestřába."
        canonicalUrl={typeof window !== 'undefined' ? window.location.href : undefined}
      />

      <div className="mr-auto max-w-7xl space-y-6 px-3 py-6 sm:px-6">
        <section className="grid gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(320px,0.95fr)]">
          <form onSubmit={handleImport} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex items-start gap-3">
              <span className="rounded-xl bg-violet-50 p-2.5 text-violet-700">
                <Upload className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Přidat kontakty</h2>
                <p className="mt-1 text-sm text-slate-500">Jedna adresa na řádek, případně oddělená čárkou nebo středníkem.</p>
              </div>
            </div>

            <label className="mt-5 block text-sm font-medium text-slate-700" htmlFor="outreach-emails">
              E-mailové adresy
            </label>
            <textarea
              id="outreach-emails"
              rows={8}
              value={emailInput}
              onChange={(event) => setEmailInput(event.target.value)}
              placeholder={'makler@example.cz\ndalsi@realitka.cz'}
              className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-3 font-mono text-sm outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
            />

            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                {parsedInput.emails.length} unikátních kontaktů
              </span>
              {parsedInput.invalidEmails.length > 0 && (
                <span className="rounded-full bg-red-50 px-2.5 py-1 text-red-700">
                  {parsedInput.invalidEmails.length} neplatných
                </span>
              )}
              {parsedInput.emails.length > 500 && (
                <span className="text-red-700">Maximum je 500 kontaktů v jednom importu.</span>
              )}
            </div>

            {parsedInput.invalidEmails.length > 0 && (
              <p className="mt-2 text-xs text-red-700">Zkontrolujte: {parsedInput.invalidEmails.join(', ')}</p>
            )}

            <div className="mt-5 grid gap-4 sm:grid-cols-[1fr_180px]">
              <div>
                <label className="block text-sm font-medium text-slate-700" htmlFor="outreach-source">
                  Zdroj souhlasu
                </label>
                <input
                  id="outreach-source"
                  type="text"
                  maxLength={160}
                  value={sourceLabel}
                  onChange={(event) => setSourceLabel(event.target.value)}
                  placeholder="Např. Odhad zdarma – srpen 2026"
                  className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700" htmlFor="outreach-consent-date">
                  Datum souhlasu
                </label>
                <input
                  id="outreach-consent-date"
                  type="date"
                  max={formatLocalDateInput()}
                  value={consentCapturedAt}
                  onChange={(event) => setConsentCapturedAt(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                />
              </div>
            </div>

            <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <input
                type="checkbox"
                checked={consentConfirmed}
                onChange={(event) => setConsentConfirmed(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-emerald-400 text-emerald-700 focus:ring-emerald-500"
              />
              <span className="text-sm leading-5 text-emerald-950">
                Potvrzuji, že tyto kontakty prokazatelně souhlasily se zasíláním obchodních sdělení z Realitního Jestřába.
              </span>
            </label>

            <button
              type="submit"
              disabled={!canImport}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-violet-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              <Upload className="h-4 w-4" />
              {importing ? 'Importuji…' : `Importovat ${parsedInput.emails.length || ''} kontaktů`}
            </button>
          </form>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-950 p-5 text-white shadow-sm sm:p-6">
              <div className="flex items-center gap-3">
                <span className="rounded-xl bg-white/10 p-2.5 text-lime-300"><ClipboardList className="h-5 w-5" /></span>
                <div>
                  <div className="text-xs font-medium uppercase tracking-[0.16em] text-slate-400">Fronta kontaktů</div>
                  <div className="mt-1 text-3xl font-bold">{pagination.total.toLocaleString('cs-CZ')}</div>
                </div>
              </div>
              <p className="mt-4 text-sm leading-6 text-slate-300">
                Import vytváří bezpečnou kontaktní frontu. Databáze inzerátů se prohledá až po ručním kliknutí na Připravit a nic se neodesílá.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                <div>
                  <h3 className="font-semibold text-slate-950">Bezpečný první krok</h3>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    Duplicitní adresa nevytvoří druhý kontakt ani nepřepíše původní záznam souhlasu. Příprava uloží jen omezený snapshot posledního inzerátu a souhrny odhadu.
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </section>

        {result && (
          <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
            <span>Přidáno {result.imported}, již existovalo {result.existing}. Celkem zpracováno {result.submitted} kontaktů.</span>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">Kontaktní fronta</h2>
              <p className="text-sm text-slate-500">Kontakty a jejich ověřený podklad pro budoucí individuální oslovení.</p>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Hledat e-mail…"
                className="w-full rounded-xl border border-slate-300 py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-3 font-medium">Kontakt</th>
                  <th className="px-5 py-3 font-medium">Poslední inzerát</th>
                  <th className="px-5 py-3 font-medium">Odhad</th>
                  <th className="px-5 py-3 font-medium">Souhlas</th>
                  <th className="px-5 py-3 font-medium">Akce</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {contacts.map((contact) => (
                  <tr key={contact._id} className="hover:bg-slate-50/70">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <span className="rounded-lg bg-violet-50 p-2 text-violet-700"><Mail className="h-4 w-4" /></span>
                        <div>
                          <div className="font-medium text-slate-900">{contact.email}</div>
                          <div className="mt-0.5 text-xs text-slate-500">{contact.source?.label || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4"><ResearchListing research={contact.research} /></td>
                    <td className="px-5 py-4">
                      {contact.research?.status === 'listing_ready'
                        ? <ValuationEvidence valuation={contact.research.valuation} />
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-5 py-4">
                      <div className="font-medium text-emerald-700">Potvrzený</div>
                      <div className="mt-0.5 text-xs text-slate-500">{formatDate(contact.consent?.capturedAt)}</div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-col items-start gap-2">
                        <button
                          type="button"
                          disabled={preparingContactId === contact._id}
                          onClick={() => handlePrepareContact(contact._id)}
                          className="inline-flex items-center gap-2 rounded-lg border border-violet-200 px-3 py-2 text-xs font-medium text-violet-700 transition hover:bg-violet-50 disabled:cursor-wait disabled:opacity-50"
                        >
                          <RefreshCw className={`h-3.5 w-3.5 ${preparingContactId === contact._id ? 'animate-spin' : ''}`} />
                          {preparingContactId === contact._id
                            ? 'Připravuji…'
                            : contact.research?.status === 'not_started' || !contact.research
                              ? 'Připravit'
                              : 'Obnovit'}
                        </button>
                        {contact.research?.status === 'listing_ready' && (
                          <button
                            type="button"
                            onClick={() => setSelectedContactId(contact._id)}
                            className="inline-flex items-center gap-2 rounded-lg bg-slate-950 px-3 py-2 text-xs font-medium text-white transition hover:bg-slate-800"
                          >
                            <Sparkles className="h-3.5 w-3.5" />
                            {contact.draft ? 'Otevřít návrh' : 'Vytvořit návrh'}
                          </button>
                        )}
                      </div>
                      <div className="mt-2 text-xs text-slate-400">{stageLabels[contact.stage] || contact.stage}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!loading && contacts.length === 0 && (
              <div className="px-5 py-16 text-center">
                <Mail className="mx-auto h-8 w-8 text-slate-300" />
                <div className="mt-3 font-medium text-slate-700">Zatím žádné kontakty</div>
                <p className="mt-1 text-sm text-slate-500">Vložte první testovací e-mail výše.</p>
              </div>
            )}
            {loading && <div className="px-5 py-12 text-center text-sm text-slate-500">Načítám kontakty…</div>}
          </div>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 px-5 py-4 text-sm">
              <span className="text-slate-500">Strana {pagination.page} z {pagination.pages}</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={pagination.page <= 1 || loading}
                  onClick={() => setPagination((current) => ({ ...current, page: current.page - 1 }))}
                  className="rounded-lg border border-slate-300 px-3 py-2 disabled:opacity-40"
                >
                  Předchozí
                </button>
                <button
                  type="button"
                  disabled={pagination.page >= pagination.pages || loading}
                  onClick={() => setPagination((current) => ({ ...current, page: current.page + 1 }))}
                  className="rounded-lg border border-slate-300 px-3 py-2 disabled:opacity-40"
                >
                  Další
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      {selectedContact && (
        <DraftWorkspace
          contact={selectedContact}
          onClose={() => setSelectedContactId(null)}
          onContactUpdated={handleContactUpdated}
        />
      )}
    </Layout>
  )
}

export default Outreach
